import type { Announcement } from '../api/announcement.service';
import type { SessionAttendance } from '../api/attendance.service';
import type { ClassSchedule } from '../api/class-schedule.service';
import type { ClassSession } from '../api/class-session.service';
import type { SportEvent } from '../api/event.service';
import type { GroupEnrollment } from '../api/group-enrollment.service';
import type { Group } from '../api/group.service';
import type { Location } from '../api/location.service';
import type { TenantMonthlyPayment, PaymentStatus } from '../api/payments.service';
import type {
  MemberGender,
  MemberStatus,
  MemberPaymentsAnnotation,
  TenantMemberResponse,
} from '../api/tenant-members.service';
import type { TenantPublicDto } from '../api/tenant-public.service';
import type { TenantAccountRole } from '../auth/tenant-auth.service';

export interface DemoMember {
  readonly id: string;
  readonly email: string | null;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string | null;
  readonly roles: readonly TenantAccountRole[];
  readonly status: MemberStatus;
  readonly isClaimed: boolean;
  readonly pending: boolean;
  readonly createdAt: string;
  readonly weightKg: number | null;
  readonly heightCm: number | null;
  readonly birthdate: string | null;
  readonly gender: MemberGender | null;
  readonly profilePictureUrl: string | null;
}

/**
 * Computes the display name from a member's name parts, last name first to
 * match the backend's `fullName` contract (e.g. "Popescu Andrei").
 */
export function memberFullName(member: {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
}): string {
  return [member.lastName, member.firstName].filter(Boolean).join(' ').trim();
}

export interface DemoPaymentRecord {
  readonly paymentId: string;
  readonly studentTenantAccountId: string;
  readonly year: number;
  readonly month: number;
  readonly status: PaymentStatus;
  readonly amountDue: number;
  readonly amountPaid: number | null;
  readonly paidAt: string | null;
  readonly paidMethod: TenantMonthlyPayment['paidMethod'];
  readonly note: string | null;
  readonly waivedReason: string | null;
}

export interface DemoTenant {
  readonly tenantId: string;
  readonly subdomain: string;
  readonly public: TenantPublicDto;
}

export interface DemoStoreData {
  readonly version: number;
  readonly tenant: DemoTenant;
  readonly members: readonly DemoMember[];
  readonly locations: readonly Location[];
  readonly groups: readonly Group[];
  readonly enrollments: readonly GroupEnrollment[];
  readonly schedules: readonly ClassSchedule[];
  readonly sessions: readonly ClassSession[];
  readonly attendances: readonly SessionAttendance[];
  readonly payments: readonly DemoPaymentRecord[];
  readonly events: readonly SportEvent[];
  readonly announcements: readonly Announcement[];
  readonly currentMemberId: string;
}

export type MemberKindFilter = 'coaches' | 'athletes';

export function memberMatchesKind(
  member: DemoMember,
  kind: MemberKindFilter,
): boolean {
  if (kind === 'athletes') {
    return member.roles.includes('Student');
  }
  return member.roles.some((r) => r === 'Coach' || r === 'Admin' || r === 'Owner');
}

export function primaryRole(member: DemoMember): TenantAccountRole {
  if (member.roles.includes('Owner')) return 'Owner';
  if (member.roles.includes('Admin')) return 'Admin';
  if (member.roles.includes('Coach')) return 'Coach';
  return 'Student';
}

export function toMemberResponse(
  member: DemoMember,
  payments: MemberPaymentsAnnotation | null,
): TenantMemberResponse {
  return {
    id: member.id,
    email: member.email,
    firstName: member.firstName,
    lastName: member.lastName,
    fullName: memberFullName(member),
    phone: member.phone,
    role: primaryRole(member),
    status: member.status,
    isClaimed: member.isClaimed,
    pending: member.pending,
    createdAt: member.createdAt,
    invitationExpiresAt: null,
    title: null,
    payments,
    weightKg: member.weightKg,
    heightCm: member.heightCm,
    birthdate: member.birthdate,
    gender: member.gender,
    profilePictureUrl: member.profilePictureUrl,
    paymentReference: null,
    lastSeenAt: null,
    karateBeltId: null,
  };
}
